import { eq } from "drizzle-orm";
import {
  meal_plan_entry,
  type PowerSyncDrizzleDB,
  recipe,
  recipe_ingredient,
} from "@mealplan/powersync";
import { nowISO } from "../utils/date-helpers";
import {
  patchRowVerified,
  type VerifiedWriteOptions,
  waitForRowPresent,
} from "../powersync/verified-write";
import { generateId } from "@fleet/utils";
import { parseStringArray, serializeStringArray } from "./queries";

/**
 * Every `UPDATE … WHERE id = ?` in this file goes through `patchRowVerified` (or,
 * for the `updateRecipeMutation` transaction, through `waitForRowPresent`
 * before entering): a raw UPDATE against the PowerSync view can match
 * ZERO rows and report success while the newly created row is still in transit to
 * the server — exactly the flow of creating a recipe and immediately editing it
 * (see `../powersync/verified-write.ts`). Mutations **can throw**: the
 * caller must propagate it, never swallow it.
 */

export interface RecipeIngredientInput {
  /** Existing id (edit) or undefined (new). */
  id?: string;
  name: string;
  quantity: number | null;
  unit: string | null;
  aisleId?: string | null;
}

/** Nutrition PER SERVING of the recipe (00052). All optional. */
export interface RecipeNutritionInput {
  calories: number | null;
  proteinG: number | null;
  carbsG: number | null;
  fatG: number | null;
}

export interface RecipeInput {
  name: string;
  servings: number;
  steps: string[];
  tags: string[];
  notes?: string | null;
  photoUrl?: string | null;
  ingredients: RecipeIngredientInput[];
  /**
   * Nutrition per serving. `undefined` = do NOT touch the existing one (e.g.
   * restoring an L4 version preserves the current nutrition — not versioned);
   * an object (or null fields) = write exactly those values.
   */
  nutrition?: RecipeNutritionInput;
}

const DEFAULT_SERVINGS = 2;

function sanitizeServings(servings: number): number {
  if (!Number.isFinite(servings) || servings <= 0) {
    return DEFAULT_SERVINGS;
  }
  return Math.round(servings);
}

/** Creates a recipe with its ingredients in a local transaction. */
export async function createRecipeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  input: RecipeInput,
): Promise<string> {
  const id = generateId();
  const now = nowISO();

  await db.transaction(async (trx) => {
    await trx.insert(recipe).values({
      id,
      user_id: userId,
      name: input.name.trim(),
      photo_url: input.photoUrl ?? null,
      servings: sanitizeServings(input.servings),
      steps: serializeStringArray(input.steps),
      tags: serializeStringArray(input.tags),
      notes: input.notes?.trim() || null,
      calories: input.nutrition?.calories ?? null,
      protein_g: input.nutrition?.proteinG ?? null,
      carbs_g: input.nutrition?.carbsG ?? null,
      fat_g: input.nutrition?.fatG ?? null,
      created_at: now,
      updated_at: now,
    });

    let order = 0;
    for (const ingredientInput of input.ingredients) {
      if (!ingredientInput.name.trim()) {
        continue;
      }
      await trx.insert(recipe_ingredient).values({
        id: generateId(),
        recipe_id: id,
        user_id: userId,
        name: ingredientInput.name.trim(),
        quantity: ingredientInput.quantity,
        unit: ingredientInput.unit?.trim() || null,
        aisle_id: ingredientInput.aisleId ?? null,
        sort_order: order,
        created_at: now,
        updated_at: now,
      });
      order += 1;
    }
  });

  return id;
}

/**
 * Updates the recipe and REPLACES its ingredient set (a simple strategy
 * for the F1 editor: deletes those no longer present and upserts the rest).
 */
export async function updateRecipeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  recipeId: string,
  input: RecipeInput,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const now = nowISO();

  // The transaction below CANNOT retry (it would sleep with the write
  // lock held) and the damage from touching a missing row is worse than an
  // UPDATE that matches nothing: the sibling ingredient DELETE/INSERT WOULD
  // still happen, leaving the recipe with new ingredients but the name and
  // servings unchanged. Therefore the wait is OUTSIDE, before entering.
  await waitForRowPresent(db, recipe, recipe.id, recipeId, options);

  await db.transaction(async (trx) => {
    await trx
      .update(recipe)
      .set({
        name: input.name.trim(),
        photo_url: input.photoUrl ?? null,
        servings: sanitizeServings(input.servings),
        steps: serializeStringArray(input.steps),
        tags: serializeStringArray(input.tags),
        notes: input.notes?.trim() || null,
        // `nutrition === undefined` does NOT touch the existing nutrition (L4 restore).
        ...(input.nutrition !== undefined && {
          calories: input.nutrition.calories,
          protein_g: input.nutrition.proteinG,
          carbs_g: input.nutrition.carbsG,
          fat_g: input.nutrition.fatG,
        }),
        updated_at: now,
      })
      .where(eq(recipe.id, recipeId));

    const existing = await trx
      .select({ id: recipe_ingredient.id })
      .from(recipe_ingredient)
      .where(eq(recipe_ingredient.recipe_id, recipeId));
    const keptIds = new Set(
      input.ingredients.map((item) => item.id).filter((id): id is string => Boolean(id)),
    );

    for (const row of existing) {
      if (!keptIds.has(row.id)) {
        await trx.delete(recipe_ingredient).where(eq(recipe_ingredient.id, row.id));
      }
    }

    let order = 0;
    for (const ingredientInput of input.ingredients) {
      const name = ingredientInput.name.trim();
      if (!name) {
        continue;
      }
      const values = {
        name,
        quantity: ingredientInput.quantity,
        unit: ingredientInput.unit?.trim() || null,
        aisle_id: ingredientInput.aisleId ?? null,
        sort_order: order,
        updated_at: now,
      };
      if (ingredientInput.id) {
        await trx
          .update(recipe_ingredient)
          .set(values)
          .where(eq(recipe_ingredient.id, ingredientInput.id));
      } else {
        await trx.insert(recipe_ingredient).values({
          id: generateId(),
          recipe_id: recipeId,
          user_id: userId,
          created_at: now,
          ...values,
        });
      }
      order += 1;
    }
  });
}

/** Ingredient as it travels inside the `ingredients` snapshot of a
 * version (`[{name, quantity, unit, aisle_id, sort_order}, …]`). */
interface VersionIngredientNode {
  name: string;
  quantity: number | null;
  unit: string | null;
  aisle_id: string | null;
  sort_order: number;
}

/** State of an (L4) version needed to restore: parent fields +
 * serialized ingredients (local bucket or API camelCase→snake_case). */
export interface RecipeVersionSnapshot {
  name: string | null;
  servings: number | null;
  steps: string | null;
  tags: string | null;
  notes: string | null;
  photo_url: string | null;
  ingredients: string | null;
}

function isVersionIngredientNode(value: unknown): value is VersionIngredientNode {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const node = value as Partial<Record<keyof VersionIngredientNode, unknown>>;
  return typeof node.name === "string";
}

/** Parses the `ingredients` snapshot (JSON from the local bucket or API). */
export function parseVersionIngredients(ingredients: string | null): VersionIngredientNode[] {
  if (!ingredients) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(ingredients);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(isVersionIngredientNode).map((node) => ({
      name: node.name,
      quantity: typeof node.quantity === "number" ? node.quantity : null,
      unit: typeof node.unit === "string" ? node.unit : null,
      aisle_id: typeof node.aisle_id === "string" ? node.aisle_id : null,
      sort_order: typeof node.sort_order === "number" ? node.sort_order : 0,
    }));
  } catch {
    return [];
  }
}

/**
 * Restores a history version (L4): a NORMAL forward write.
 *
 * Reuses `updateRecipeMutation` (restores the parent fields + REPLACES the
 * ingredients with those from the snapshot) in ONE transaction. History is never
 * rewritten: the server write-path archives the CURRENT state as a new
 * version BEFORE applying this (the pre-restore state is preserved in a
 * natural way, without collapse). It does NOT touch `deleted_at` (trash intact).
 */
export async function restoreRecipeVersionMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  recipeId: string,
  version: RecipeVersionSnapshot,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const ingredients = parseVersionIngredients(version.ingredients)
    .toSorted((a, b) => a.sort_order - b.sort_order)
    .map((node) => ({
      name: node.name,
      quantity: node.quantity,
      unit: node.unit,
      aisleId: node.aisle_id,
    }));
  const input: RecipeInput = {
    name: version.name ?? "",
    servings: version.servings ?? DEFAULT_SERVINGS,
    steps: parseStringArray(version.steps),
    tags: parseStringArray(version.tags),
    notes: version.notes ?? null,
    photoUrl: version.photo_url ?? null,
    ingredients,
  };
  await updateRecipeMutation(db, userId, recipeId, input, options);
}

/**
 * L2 trash: moves the recipe to the trash (soft-delete). It does NOT delete its
 * ingredients or its planner entries: when restoring, the slots that
 * reference it resolve again. Live views hide it (deleted_at
 * IS NULL); shopping-list generation does not use it either.
 */
export async function trashRecipeMutation(
  db: PowerSyncDrizzleDB,
  recipeId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, recipe, recipe.id, recipeId, { deleted_at: nowISO() }, options);
}

/** Restores a recipe from the trash (deleted_at → NULL). */
export async function restoreRecipeMutation(
  db: PowerSyncDrizzleDB,
  recipeId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, recipe, recipe.id, recipeId, { deleted_at: null }, options);
}

/**
 * PERMANENT deletion ("eliminar para siempre" from the trash): deletes the
 * recipe, its ingredients, and its plan entries in cascade. Irreversible.
 */
export async function deleteRecipeMutation(
  db: PowerSyncDrizzleDB,
  recipeId: string,
): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(meal_plan_entry).where(eq(meal_plan_entry.recipe_id, recipeId));
    await trx.delete(recipe_ingredient).where(eq(recipe_ingredient.recipe_id, recipeId));
    await trx.delete(recipe).where(eq(recipe.id, recipeId));
  });
}
